/**
 * @file This file contains the assignments store.
 */
import * as api from '@codegrade/api';
import * as ip from '@codegrade/ip-address';
import { Maybe } from '@codegrade/maybe';
import { array } from '@codegrade/utils';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import client from '@/client';
import * as models from '@/models';
import { Assignment } from '@/models';
import { CoursesStore, GroupSetsStore, RubricsStore, UserStore } from '@/store';
import * as utils from '@/utils';
import { AutoTestStore } from './autotest';
const storeBuilder = getStoreBuilder();
/**
 * Creates a unique string key for a group set within a course.
 *
 * This key is used for caching group sets in a flat dictionary, combining the
 * course and group set IDs into a single identifier.
 *
 * @param root0 The input object.
 * @returns The formatted string key (e.g., '123::456'), cast to a `GroupSetKey`.
 */
function makeByGroupSetKey({ courseId, groupSetId, }) {
    return `${courseId}::${groupSetId}`;
}
/** @returns The initial state of the store. */
function makeInitialState() {
    return {
        fullAssignments: {},
        fullAssignmentLoaders: {},
        assignmentsByCourse: {},
        assignmentsByGroupSet: {},
        assignmentTimeframes: {},
        timeframeLoaders: {},
        ipRangesByAssignment: {},
        passwordByAssignment: {},
        passwordByAssignmentLoaders: {},
    };
}
const moduleBuilder = storeBuilder.module('assignments', makeInitialState());
/** The Assignment store module. */
export var AssignmentsStore;
(function (AssignmentsStore) {
    /** Commits a single assignment to the state, updating all relevant caches. */
    AssignmentsStore.commitAssignment = moduleBuilder.commit((state, { assignment, }) => {
        // Update the cache of all known assignments.
        utils.vueSet(state.fullAssignments, assignment.id, assignment);
        // If the cache for the assignment's course exists, update it as well.
        const ofCourse = state.assignmentsByCourse[assignment.courseId];
        if (ofCourse?.data != null) {
            utils.vueSet(state.assignmentsByCourse, assignment.courseId, {
                ...ofCourse,
                data: ofCourse.data.map(assig => {
                    if (assig.id === assignment.id) {
                        return assignment;
                    }
                    return assig;
                }),
            });
        }
    }, 'commitAssignment');
    /**
     * Gets a single assignment by its ID, with a fallback mechanism.
     *
     * This getter first checks for a fully-loaded `models.Assignment`. If one is not
     * found and a `courseId` is provided, it will fall back to searching the paginated
     * list for that course. Note that the fallback search may return a partial
     * `api.Assignment` object, which contains less data.
     */
    AssignmentsStore.getAssignment = moduleBuilder.read(state => 
    /**
     * The actual getter function.
     *
     * @param assignmentId The ID of the assignment to retrieve.
     * @param root1 The payload object.
     * @returns A `Maybe` containing the assignment if found. The result may be a
     * full `models.Assignment` or a partial `api.Assignment`.
     */
    (assignmentId, { courseId, }) => {
        const found = state.fullAssignments[assignmentId];
        if (found != null) {
            return Maybe.of(found);
        }
        else if (courseId != null) {
            const byCourse = state.assignmentsByCourse[courseId];
            if (byCourse != null) {
                return array.findMaybe(assig => assig.id === assignmentId, byCourse.data ?? []);
            }
        }
        return Maybe.empty();
    }, 'getAssignment');
    /**
     * Updates a specific assignment in the store with partial data.
     *
     * Note: This function will only apply the update if a full `models.Assignment`
     * object is already present in the cache. It will not update a partial
     * `api.Assignment` object found via fallback search.
     *
     * @param root0 The payload for the update.
     */
    function commitUpdateAssignment({ assignmentId, update, }) {
        const assig = AssignmentsStore.getAssignment()(assignmentId, {}).extract();
        if (assig instanceof models.Assignment) {
            AssignmentsStore.commitAssignment({
                assignment: new models.Assignment({ ...assig, ...update }),
            });
        }
    }
    AssignmentsStore.commitUpdateAssignment = commitUpdateAssignment;
    /** Adds or removes a loader promise for a single assignment. */
    const commitAssignmentLoader = moduleBuilder.commit((state, { assignmentId, loader, }) => {
        utils.vueSetOrDelete(state.fullAssignmentLoaders, assignmentId, loader);
    }, 'commitAssignmentLoader');
    /** Commits a paginated list of assignments for a specific course. */
    AssignmentsStore.commitCourseAssignments = moduleBuilder.commit((state, { courseId, assignments, }) => {
        utils.vueSet(state.assignmentsByCourse, courseId, assignments);
    }, 'commitCourseAssignments');
    /** Clear the entire store. */
    AssignmentsStore.commitClearAll = moduleBuilder.commit(state => {
        Object.assign(state, makeInitialState());
    }, 'commitClearAll');
    /** Gets the paginated list of assignments for a given course. */
    AssignmentsStore.getCourseAssignments = moduleBuilder.read(state => 
    /**
     * The actual getter function.
     *
     * @param courseId The ID of the course.
     * @returns A paginated state object containing the assignments.
     */
    (courseId) => utils.getPaginatedState(state.assignmentsByCourse[courseId]), 'getCourseAssignments');
    /** Gets the paginated list of assignments for a given group set within a course. */
    AssignmentsStore.getGroupSetAssignments = moduleBuilder.read(state => 
    /**
     * The actual getter function.
     *
     * @param courseId The ID of the course containing the group set.
     * @param groupSetId The ID of the group set.
     * @returns A paginated state object containing the assignments.
     */
    (courseId, groupSetId) => utils.getPaginatedState(state.assignmentsByGroupSet[makeByGroupSetKey({ courseId, groupSetId })]), 'getGroupSetAssignments');
    // eslint-disable-next-line
    function _getAssignmentTimeframe(state) {
        return (assignmentId) => Maybe.fromNullable(state.assignmentTimeframes[assignmentId]);
    }
    const commitTimeframeLoader = moduleBuilder.commit((state, { assignmentId, maybeLoader, }) => {
        maybeLoader.caseOf({
            Just: loader => {
                utils.vueSet(state.timeframeLoaders, assignmentId, loader);
            },
            Nothing: () => utils.vueDelete(state.timeframeLoaders, assignmentId),
        });
    }, 'commitTimeframeLoader');
    const addTimeframe = moduleBuilder.commit((state, { assignmentId, data, }) => {
        utils.vueSet(state.assignmentTimeframes, assignmentId, data);
    }, 'addTimeframe');
    /** Get the timeframes connected to an assignment. */
    AssignmentsStore.getTimeframe = moduleBuilder.read(state => 
    /**
     * Get the timeframes connected to an assignment.
     *
     * @param assignmentId The id of the assignment to get the timeframes for.
     * @returns The timeframes connected to the given assignment, if they have already been loaded.
     */
    (assignmentId) => Maybe.fromNullable(state.assignmentTimeframes[assignmentId]), 'getTimeframe');
    /** Load the timeframes connected to an assignment. */
    AssignmentsStore.loadAssignmentTimeframe = moduleBuilder.dispatch(({ state }, { assignmentId, force = false, }) => utils.storeLoadWithMaybeForce({
        value: _getAssignmentTimeframe(state)(assignmentId),
        force,
        loader: Maybe.fromNullable(state.timeframeLoaders[assignmentId]),
        load: async () => {
            const resp = await client.p.assignment.getTimeframes({ assignmentId });
            addTimeframe({ assignmentId, data: resp.cgResult });
        },
        addLoader: loader => commitTimeframeLoader({ assignmentId, maybeLoader: Maybe.of(loader) }),
        removeLoader: () => commitTimeframeLoader({ assignmentId, maybeLoader: Maybe.empty() }),
    }), 'loadAssignmentTimeframe');
    /** Fetches and stores a paginated list of assignments for a specific course. */
    AssignmentsStore.loadAssignments = moduleBuilder.dispatch(
    /**
     * The actual action handler.
     *
     * @param root0 The module context.
     * @param root1 The action payload.
     * @returns A promise that resolves when the operation is complete.
     */
    async ({ state }, { courseId, toLoad, }) => {
        await utils.loadPaginatedWithForce({
            getData: () => state.assignmentsByCourse[courseId],
            commitData: data => AssignmentsStore.commitCourseAssignments({ courseId, assignments: data }),
            load: async (nextToken) => await client.p.course.getAssignments({ courseId, nextToken }),
            toLoad,
        });
    }, 'loadAssignments');
    /** Fetches and stores a paginated list of assignments for a specific group set. */
    AssignmentsStore.loadAssignmentsForGroupSet = moduleBuilder.dispatch(
    /**
     * The actual action handler.
     *
     * @param root0 The module context.
     * @param root1 The action payload.
     * @returns A promise that resolves when the operation is complete.
     */
    async ({ state }, { courseId, groupSetId, toLoad, }) => {
        const key = makeByGroupSetKey({ courseId, groupSetId });
        await utils.loadPaginatedWithForce({
            getData: () => state.assignmentsByGroupSet[key],
            load: nextToken => client.p.course.getAssignments({ courseId, groupSetId, nextToken }),
            commitData: data => utils.vueSet(state.assignmentsByGroupSet, key, data),
            toLoad,
        });
    }, 'loadGroupSetAssignments');
    /** Action to load data of a single assignment. */
    AssignmentsStore.loadSingleAssignment = moduleBuilder.dispatch(async ({ state }, { assignmentId, force = false }) => {
        await utils.storeLoadWithMaybeForce({
            value: Maybe.fromNullable(state.fullAssignments[assignmentId]),
            loader: Maybe.fromNullable(state.fullAssignmentLoaders[assignmentId]),
            addLoader: loader => commitAssignmentLoader({ assignmentId, loader: Maybe.of(loader) }),
            removeLoader: () => commitAssignmentLoader({ assignmentId, loader: Maybe.empty() }),
            force,
            load: async () => {
                const { body: assignment } = await client.p.assignment.get({ assignmentId });
                AssignmentsStore.commitAssignment({
                    assignment: new models.Assignment(assignment),
                });
            },
        });
    }, 'loadSingleAssignment');
    /** The action to reorder assignments of a course. */
    AssignmentsStore.reorderCourseAssignments = moduleBuilder.dispatch(async (_, { courseId, assignmentIds, }) => {
        await client.p.course.reorderAssignments({
            courseId,
            requestBody: {
                assignmentIds,
            },
        });
        await AssignmentsStore.loadAssignments({ courseId, toLoad: 'force' });
    }, 'reorderCourseAssignments');
    /** Update the timeframes of an assignment. */
    AssignmentsStore.putTimeframes = moduleBuilder.dispatch(async (_, { assignmentId, timeframes, }) => {
        const putRes = await client.p.assignment.putTimeframes({
            assignmentId,
            requestBody: timeframes,
        });
        await AssignmentsStore.loadSingleAssignment({
            assignmentId,
            force: true,
        });
        return putRes.addCallbacks({
            onAfterSuccess: () => {
                addTimeframe({ assignmentId, data: putRes.cgResult });
            },
        });
    }, 'putTimeframe');
    /** Action to patch an assignment. */
    AssignmentsStore.patchAssignment = moduleBuilder.dispatch(async (_, payload) => {
        const { assignmentId, assignmentProps, submitTypes } = payload;
        const templateRes = submitTypes == null
            ? api.ApiResult.fromResponseAndValue(new Response(), null)
            : await client.p.assignment.patchSubmitTypes({
                assignmentId,
                requestBody: {
                    json: {
                        filesUploadEnabled: submitTypes.files,
                        webhookConfiguration: submitTypes.webhookConfiguration,
                        editorUploadEnabled: submitTypes.editor,
                    },
                    template: submitTypes.template?.map(x => x),
                },
            });
        const patchRes = await client.p.assignment.patch({
            assignmentId,
            requestBody: assignmentProps,
        });
        return patchRes.addWarnings(templateRes).addCallbacks({
            /** Commit the patch response data to the store. */
            onAfterSuccess() {
                AssignmentsStore.commitAssignment({
                    assignment: new Assignment(patchRes.cgResult),
                });
            },
        });
    }, 'patchAssignment');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AssignmentsStore.createAssignment = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    async (_, { courseId, name }) => {
        const response = await client.course
            .createAssignment({
            courseId,
            requestBody: { name },
        })
            .then(res => res.unsafeCoerce());
        const assignment = new Assignment(response.cgResult);
        return response.addCallbacks({
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            onAfterSuccess() {
                AssignmentsStore.commitAssignment({ assignment });
            },
        });
    }, 'createAssignment');
    /** The dispatch to do after you have import into an assignment. */
    AssignmentsStore.afterImport = moduleBuilder.dispatch((_, { intoAssignment, }) => Promise.all([
        RubricsStore.loadRubric({ assignmentId: intoAssignment.id, force: true }),
        AutoTestStore.loadAutoTestByAssignment({
            assignmentId: intoAssignment.id,
            force: true,
        }).catch(utils.tryHandleApiError({
            404: () => null,
            403: () => null,
            // For others: propagate the error.
            _: err => {
                throw err;
            },
        })),
    ]), 'afterImport');
    /** Copy the target assignment into the source. */
    AssignmentsStore.copyAssignment = moduleBuilder.dispatch(async (_, payload) => {
        const response = await client.p.assignment.importInto({
            intoAssignmentId: payload.into.id,
            requestBody: {
                fromAssignmentId: payload.from.id,
                allowExistingSubmissions: payload.allowExistingSubmissions === true,
            },
        });
        await Promise.all([
            AssignmentsStore.afterImport({ intoAssignment: payload.into }),
            CoursesStore.loadSingleCourse({
                courseId: payload.into.courseId,
                force: true,
            }),
            GroupSetsStore.loadForCourse({
                courseId: payload.into.courseId,
                toLoad: 'force',
            }),
        ]);
        AssignmentsStore.commitAssignment({
            assignment: new models.Assignment(response.body),
        });
        return response;
    }, 'copyAssignment');
    /** Gets the IP ranges. */
    AssignmentsStore.getIPRanges = moduleBuilder.read(state => 
    /**
     * Get the IP ranges for an assignment.
     *
     * @param assignmentId The ID of the assignment.
     * @returns A paginated state object containing the IP ranges.
     */
    (assignmentId) => utils.getPaginatedState(state.ipRangesByAssignment[assignmentId]), 'getIPRanges');
    const commitIPRanges = moduleBuilder.commit((state, { assignmentId, newState, }) => {
        const currentState = state.ipRangesByAssignment[assignmentId];
        const deduped = {
            pagination: { tag: 'not-started' },
            ...currentState,
            ...newState,
            data: Array.from(utils.itertools.unique(item => ip.cidr.formatForHuman(item.ipRange), newState.data ?? currentState?.data ?? [])),
        };
        utils.vueSet(state.ipRangesByAssignment, assignmentId, deduped);
    }, 'commitIPRanges');
    /** Loads a page of IP ranges. */
    AssignmentsStore.loadIPRanges = moduleBuilder.dispatch(async ({ state }, { 
    /** The ID of the assignment. */
    assignmentId, 
    /** The paginated load value. */
    toLoad, }) => {
        await utils.loadPaginatedWithForce({
            getData: () => state.ipRangesByAssignment[assignmentId],
            commitData: newState => commitIPRanges({ assignmentId, newState }),
            load: async (nextToken) => await client.p.assignment.getAllAllowedIps({ assignmentId, nextToken }),
            toLoad,
        });
    }, 'loadIPRanges');
    /** Adds an IP range. */
    AssignmentsStore.addIPRange = moduleBuilder.dispatch(async ({ state }, { assignmentId, ipRange, }) => {
        const response = await client.p.assignment.putAllowedIp({
            assignmentId,
            ipRange,
        });
        const currentState = state.ipRangesByAssignment[assignmentId];
        return response.addCallbacks({
            /** Commit the IP ranges after succcessful add. */
            onAfterSuccess() {
                commitIPRanges({
                    assignmentId,
                    newState: {
                        data: [
                            { ipRange: response.cgResult.ipRange },
                            ...(currentState?.data ?? []),
                        ],
                    },
                });
            },
        });
    }, 'addIPRange');
    /** Deletes an IP range. */
    AssignmentsStore.deleteIPRange = moduleBuilder.dispatch(async ({ state }, { assignmentId, ipRange: ipRangeToDelete, }) => {
        const response = await client.p.assignment.deleteAllowedIp({
            assignmentId,
            ipRange: ipRangeToDelete,
        });
        const current = state.ipRangesByAssignment[assignmentId];
        return response.addCallbacks({
            /** Commit ranges after successful update. */
            onAfterSuccess() {
                commitIPRanges({
                    assignmentId,
                    newState: {
                        data: (current?.data ?? []).filter(item => item.ipRange !== ipRangeToDelete),
                    },
                });
            },
        });
    }, 'deleteIPRange');
    /** Updates an IP range. This is just a combination of delete and add. */
    AssignmentsStore.updateIPRange = moduleBuilder.dispatch(async ({ state }, { assignmentId, from, to, }) => {
        const current = state.ipRangesByAssignment[assignmentId];
        const currentData = current?.data ?? [];
        const idxItemUpdated = currentData.findIndex(item => item.ipRange === from);
        if (idxItemUpdated === -1) {
            throw new Error(`Fail to edit: cannot find the edited item "${from}" in the list. `);
        }
        /*
        We cannot wrap these two calls in a Promise.all due to the normalization happening in the backend.

        If this runs in parallel, deletion can happen later than creation.
        We could accidentally delete the item we just created.

        This will happen if the user changes a rule with value 0.0.0.0/0 to 9.9.9.9/0.

        We can first create 9.9.9.9/0 which normalized to 0.0.0.0/0 and the server will just have one single item 0.0.0.0/0

        When the delete request comes, it will remove 0.0.0.0/0 and the list will result in not having 0.0.0.0/0 nor 9.9.9.9/0.
        */
        await client.p.assignment.deleteAllowedIp({
            assignmentId,
            ipRange: from,
        });
        const res = await client.p.assignment.putAllowedIp({
            assignmentId,
            ipRange: to,
        });
        return res.addCallbacks({
            /** Commit ranges after successful update. */
            onAfterSuccess() {
                commitIPRanges({
                    assignmentId,
                    newState: {
                        data: [
                            ...currentData.slice(0, idxItemUpdated),
                            { ipRange: res.cgResult.ipRange },
                            ...currentData.slice(idxItemUpdated + 1),
                        ],
                    },
                });
            },
        });
    }, 'updateIPRange');
    /** Gets the password for an assignment. */
    AssignmentsStore.getPassword = moduleBuilder.read(state => 
    /**
     * @param assignmentId The ID of the assignment.
     * @returns A doubly-nested Maybe of the password.
     */
    (assignmentId) => Maybe.fromNullable(state.passwordByAssignment[assignmentId]), 'getPassword');
    /** Commits the password state for an assignment. */
    const commitPassword = moduleBuilder.commit((state, { assignmentId, password, }) => {
        utils.vueSet(state.passwordByAssignment, assignmentId, password);
    }, 'commitPassword');
    /** Adds or removes a loader promise for an assignment's password. */
    const commitPasswordLoader = moduleBuilder.commit((state, { assignmentId, loader, }) => {
        utils.vueSetOrDelete(state.passwordByAssignmentLoaders, assignmentId, loader);
    }, 'commitPasswordLoader');
    /** Loads the password for an assignment. */
    AssignmentsStore.loadPassword = moduleBuilder.dispatch(async ({ state }, { assignmentId, force = false, }) => {
        await utils.storeLoadWithMaybeForce({
            value: AssignmentsStore.getPassword()(assignmentId),
            loader: Maybe.fromNullable(state.passwordByAssignmentLoaders[assignmentId]),
            force,
            load: async () => {
                const { body } = await client.p.assignment.getPassword({
                    assignmentId,
                });
                commitPassword({ assignmentId, password: body.password });
            },
            addLoader: loader => commitPasswordLoader({ assignmentId, loader: Maybe.of(loader) }),
            removeLoader: () => commitPasswordLoader({ assignmentId, loader: Maybe.empty() }),
        });
    }, 'loadPassword');
    /** Sets or updates the password for an assignment. */
    AssignmentsStore.setPassword = moduleBuilder.dispatch(async (_, { assignmentId, password, }) => {
        const response = await client.p.assignment.putPassword({
            assignmentId,
            requestBody: { password },
        });
        commitPassword({
            assignmentId,
            password: response.cgResult.password,
        });
    }, 'setPassword');
    /** Deletes the password for an assignment. */
    AssignmentsStore.deletePassword = moduleBuilder.dispatch(async (_, { assignmentId, }) => {
        await client.p.assignment.deletePassword({ assignmentId });
        commitPassword({ assignmentId, password: Maybe.empty() });
    }, 'deletePassword');
    /** Verify the password for the locked assignment. */
    AssignmentsStore.verify = moduleBuilder.dispatch(async (_, { assignmentId, password, }) => {
        const response = await client.p.assignment.verify({
            assignmentId,
            requestBody: { password },
        });
        const newSessionData = response.cgResult;
        await UserStore.replaceSession(newSessionData);
    }, 'verify');
    utils.signal.AFTER_USER_CHANGED.registerSync(() => {
        AssignmentsStore.commitClearAll();
    });
})(AssignmentsStore || (AssignmentsStore = {}));
